import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Text,
  Badge,
  useToast,
  HStack,
  Tooltip,
  IconButton,
} from "@chakra-ui/react";
import { DownloadIcon, EmailIcon, BellIcon } from "@chakra-ui/icons";
import { jsPDF } from "jspdf";

// Utility: Get user & internship IDs from email & title
const getUserAndInternshipIds = async (email, internshipTitle) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");

    const userRes = await axios.get(
      `https://gyanbackend.aim4u.co.in/super/get-user-id/?email=${email}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    const internshipRes = await axios.get(
      `https://gyanbackend.aim4u.co.in/super/get-internship-id/?title=${internshipTitle}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    return {
      user_id: userRes.data.id,
      internship_id: internshipRes.data.id,
    };
  } catch (err) {
    console.error("Error fetching IDs:", err);
    return null;
  }
};

// Utility: Check if resume exists for given user/internship
const checkResumeExists = async (email, internshipTitle) => {
  const ids = await getUserAndInternshipIds(email, internshipTitle);
  if (!ids) return false;

  const { user_id, internship_id } = ids;

  try {
    await axios.get(
      `https://gyanbackend.aim4u.co.in/resume/view/?user_id=${user_id}&internship_id=${internship_id}`
    );
    return true;
  } catch {
    return false;
  }
};

const ViewApplication = () => {
  const [applications, setApplications] = useState([]);
  const toast = useToast();

  const generateReceipt = (app) => {
    const doc = new jsPDF();

    // Design Elements
    doc.setFillColor(22, 58, 122); // Brand Blue
    doc.rect(0, 0, 210, 40, "F");

    // Header
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("GYAN", 105, 20, { align: "center" });
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("Payment Receipt", 105, 30, { align: "center" });

    // Body Setup
    doc.setTextColor(40, 40, 40);
    doc.setFontSize(12);

    // Receipt Information Table Header
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 50, 190, 50);

    let yPos = 65;
    const addLine = (label, value) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(`${value}`, 60, yPos);
      yPos += 12;
    };

    addLine("Receipt No", `RC-${app.id}-${Date.now().toString().slice(-4)}`);
    addLine("Date", new Date().toLocaleDateString());
    addLine("Recipient", `${app.fname} ${app.lname}`);
    addLine("Email", app.user);

    yPos += 5;
    doc.line(20, yPos, 190, yPos);
    yPos += 15;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Transaction Details", 20, yPos);
    yPos += 15;

    doc.setFontSize(12);
    addLine("Internship", app.internship);
    addLine("Payment ID", app.payment_id);
    addLine("Amount Paid", `INR ${app.payment_amount}`);
    addLine("Status", app.payment_status || "COMPLETED");

    yPos += 10;
    doc.line(20, yPos, 190, yPos);
    yPos += 20;

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("This is a computer-generated receipt and requires no signature.", 105, yPos, { align: "center" });
    doc.text("Thank you for choosing Gyan!", 105, yPos + 7, { align: "center" });
    doc.text("aim4u.co.in", 105, yPos + 14, { align: "center" });

    doc.save(`Receipt_${app.fname}_${app.payment_id}.pdf`);
  };

  const sendReceiptEmail = async (app) => {
    const doc = new jsPDF();

    // Design Elements
    doc.setFillColor(22, 58, 122); // Brand Blue
    doc.rect(0, 0, 210, 40, "F");

    // Header
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("GYAN", 105, 20, { align: "center" });
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("Payment Receipt", 105, 30, { align: "center" });

    // Body Setup
    doc.setTextColor(40, 40, 40);
    doc.setFontSize(12);

    // Receipt Information Table Header
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 50, 190, 50);

    let yPos = 65;
    const addLine = (label, value) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(`${value}`, 60, yPos);
      yPos += 12;
    };

    addLine("Receipt No", `RC-${app.id}-${Date.now().toString().slice(-4)}`);
    addLine("Date", new Date().toLocaleDateString());
    addLine("Recipient", `${app.fname} ${app.lname}`);
    addLine("Email", app.user);

    yPos += 5;
    doc.line(20, yPos, 190, yPos);
    yPos += 15;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Transaction Details", 20, yPos);
    yPos += 15;

    doc.setFontSize(12);
    addLine("Internship", app.internship);
    addLine("Payment ID", app.payment_id);
    addLine("Amount Paid", `INR ${app.payment_amount}`);
    addLine("Status", app.payment_status || "COMPLETED");

    yPos += 10;
    doc.line(20, yPos, 190, yPos);
    yPos += 20;

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("This is a computer-generated receipt and requires no signature.", 105, yPos, { align: "center" });
    doc.text("Thank you for choosing Gyan!", 105, yPos + 7, { align: "center" });
    doc.text("aim4u.co.in", 105, yPos + 14, { align: "center" });

    const pdfBase64 = doc.output("datauristring");
    const accessToken = sessionStorage.getItem("accessToken");

    try {
      const backendHost = "http://localhost:8000"; // Use LOCAL for receipt sending
      await axios.post(
        `${backendHost}/super/send-receipt/`,
        {
          email: app.user,
          pdf: pdfBase64,
          filename: `Receipt_${app.fname}_${app.payment_id}.pdf`,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      toast({
        title: "Email Sent",
        description: `Receipt has been sent to ${app.user}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Email Failed",
        description: error.response?.data?.error || "Could not send the receipt email.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const sendReminderEmail = async (app) => {
    const accessToken = sessionStorage.getItem("accessToken");
    try {
      const backendHost = "http://localhost:8000";
      await axios.post(
        `${backendHost}/super/send-reminder/`,
        {
          email: app.user || app.email,
          internship: app.internship
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      toast({
        title: "Reminder Sent",
        description: `Reminder has been sent to ${app.user || app.email}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to Send Reminder",
        description: error.response?.data?.error || "Could not send the reminder email.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getApplications = async () => {
    try {
      const accessToken = sessionStorage.getItem("accessToken");
      if (!accessToken) return;

      const backendHost = "https://gyanbackend.aim4u.co.in"; // Use PROD for data fetching
      const response = await axios.get(
        `${backendHost}/super/view-applied-interns/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const enrichedApplications = await Promise.all(
        response.data.applications.map(async (app) => {
          const resumeExists = await checkResumeExists(app.user, app.internship);
          return {
            ...app,
            status: app.status || "Pending",
            resumeExists,
          };
        })
      );

      setApplications(enrichedApplications);
    } catch {
      toast({
        title: "Error",
        description: "Could not fetch applications.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getApplications();
  }, []);

  const updateStatus = async (id, newStatus, email) => {
    try {
      const accessToken = sessionStorage.getItem("accessToken");
      if (!accessToken) return;

      const backendHost = "https://gyanbackend.aim4u.co.in"; // Use PROD for status updates
      await axios.patch(
        `${backendHost}/super/update-application/${id}/`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      await axios.post(
        `${backendHost}/super/send-email/`,
        { email, status: newStatus },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setApplications((prev) =>
        prev.map((app) =>
          (app.view_app_id === id || app.id === id) ? { ...app, status: newStatus } : app
        )
      );

      toast({
        title: `Application ${newStatus}!`,
        description: `The application has been ${newStatus.toLowerCase()}.`,
        status: newStatus === "Approved" ? "success" : "error",
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Error",
        description: "Could not update application status.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleResumeAction = async (email, internshipTitle, action) => {
    const ids = await getUserAndInternshipIds(email, internshipTitle);
    if (!ids) return;

    const { user_id, internship_id } = ids;
    const baseUrl = "https://gyanbackend.aim4u.co.in/resume";
    const accessToken = sessionStorage.getItem("accessToken");

    try {
      if (action === "view" || action === "download") {
        window.open(
          `${baseUrl}/${action}/?user_id=${user_id}&internship_id=${internship_id}`,
          "_blank"
        );
      } else if (action === "delete") {
        await axios.delete(
          `${baseUrl}/delete/?user_id=${user_id}&internship_id=${internship_id}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        toast({
          title: "Resume Deleted",
          status: "info",
          duration: 3000,
          isClosable: true,
        });

        getApplications();
      }
    } catch {
      toast({
        title: "Error",
        description: `Failed to ${action} resume.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box bg="#e8f3fd" w="full" minH="100vh" p="20px">
      <Text fontSize="35px" fontWeight="bold" ml="80px" mb="20px" color="green.500">
        Applications
      </Text>

      <TableContainer bg="white" borderRadius="10px" boxShadow="lg" mx="80px">
        <Table variant="simple" colorScheme="blue">
          <Thead bg="blue.600">
            <Tr>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">ID</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Name</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Email</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Role Applied</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Resume</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Application Status</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Actions</Th>
            </Tr>
          </Thead>

          <Tbody>
            {applications.map((app, index) => (
              <Tr key={app.id} _hover={{ bg: "blue.50" }}>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.700">{index + 1}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.800" fontWeight="medium">{app.fname} {app.lname}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.700">{app.user}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.700">{app.internship}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center">
                  <HStack spacing={1} justify="center">
                    <Tooltip label="Resume not uploaded" isDisabled={app.resumeExists}>
                      <Button
                        size="xs"
                        colorScheme="blue"
                        onClick={() => handleResumeAction(app.user, app.internship, "view")}
                        isDisabled={!app.resumeExists}
                      >
                        View
                      </Button>
                    </Tooltip>
                    <Tooltip label="Resume not uploaded" isDisabled={app.resumeExists}>
                      <Button
                        size="xs"
                        colorScheme="teal"
                        onClick={() => handleResumeAction(app.user, app.internship, "download")}
                        isDisabled={!app.resumeExists}
                      >
                        Download
                      </Button>
                    </Tooltip>
                    <Tooltip label="Resume not uploaded" isDisabled={app.resumeExists}>
                      <Button
                        size="xs"
                        colorScheme="red"
                        onClick={() => handleResumeAction(app.user, app.internship, "delete")}
                        isDisabled={!app.resumeExists}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </HStack>
                </Td>
                <Td textAlign="center">
                  <HStack justify="center" spacing={2}>
                    <Badge
                      colorScheme={
                        app.status === "Approved"
                          ? "green"
                          : app.status === "Rejected"
                            ? "red"
                            : "yellow"
                      }
                    >
                      {app.status}
                    </Badge>
                    {app.status === "Approved" && app.payment_status === "COMPLETED" && (
                      <HStack spacing={1}>
                        <Tooltip label="Download Receipt">
                          <IconButton
                            size="xs"
                            icon={<DownloadIcon />}
                            colorScheme="green"
                            variant="ghost"
                            onClick={() => generateReceipt(app)}
                            aria-label="Download Receipt"
                          />
                        </Tooltip>
                        <Tooltip label="Send Receipt to Email">
                          <IconButton
                            size="xs"
                            icon={<EmailIcon />}
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => sendReceiptEmail(app)}
                            aria-label="Send Receipt Email"
                          />
                        </Tooltip>
                      </HStack>
                    )}
                    {app.payment_status !== "COMPLETED" && (
                      <Tooltip label="Send Payment Reminder">
                        <IconButton
                          size="xs"
                          icon={<BellIcon />}
                          colorScheme="yellow"
                          variant="ghost"
                          onClick={() => sendReminderEmail(app)}
                          aria-label="Send Reminder"
                        />
                      </Tooltip>
                    )}
                  </HStack>
                </Td>
                <Td textAlign="center">
                  <Button
                    colorScheme="green"
                    size="sm"
                    onClick={() => updateStatus(app.view_app_id || app.id, "Approved", app.user)}
                    isDisabled={app.status === "Approved"}
                    mr="2"
                  >
                    Approve
                  </Button>
                  <Button
                    colorScheme="red"
                    size="sm"
                    onClick={() => updateStatus(app.view_app_id || app.id, "Rejected", app.user)}
                    isDisabled={app.status === "Rejected"}
                  >
                    Reject
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ViewApplication;
